export const UNIFIED_ACCOUNT_ID = 'all' as const;

/** True if `accountId` is the unified "All accounts" sentinel. */
export function isUnifiedAccountId(accountId: string | null | undefined): boolean {
  return accountId === UNIFIED_ACCOUNT_ID;
}

/**
 * True if `folderId` is a unified folder id (e.g. 'all:inbox', 'all:custom:x').
 * Unified ids name the aggregated view, never a real per-account mailbox, so
 * they must never be passed to a per-account API call.
 */
export function isUnifiedFolderId(folderId: string | null | undefined): boolean {
  return typeof folderId === 'string' && folderId.startsWith(`${UNIFIED_ACCOUNT_ID}:`);
}

/**
 * Canonical parser for unified folder ids: 'all:inbox' → 'inbox'. Returns the
 * input unchanged when it isn't a unified id. Use THIS instead of ad-hoc
 * `slice(4)`/`startsWith` math — the prefix is derived from UNIFIED_ACCOUNT_ID.
 */
export function stripUnifiedFolderPrefix(folderId: string): string {
  return isUnifiedFolderId(folderId)
    ? folderId.slice(UNIFIED_ACCOUNT_ID.length + 1)
    : folderId;
}

/**
 * Guard at the per-account API boundary: a unified account/folder id must be
 * resolved to a real (account, mailbox) BEFORE a per-account mutation. If one
 * slips through it would silently no-op server-side, so fail loudly instead —
 * the leak is a bug to fix at the source (see resolveEmailContext), not to
 * paper over. `field` names which argument was wrong for a clear stack.
 */
export function assertRealMailboxTarget(
  value: string,
  operation: string,
  field: 'account' | 'folder' = 'folder'
): void {
  const bad = field === 'account' ? isUnifiedAccountId(value) : isUnifiedFolderId(value);
  if (bad) {
    throw new Error(
      `[unified] ${operation} received a unified ${field} id "${value}". Per-account actions ` +
        `must target a real ${field} — resolve it (resolveEmailContext) before calling the API.`
    );
  }
}

export const STANDARD_UNIFIED_ROLES = ['inbox', 'sent', 'drafts', 'spam', 'trash', 'archive'] as const;

export type StandardUnifiedRole = (typeof STANDARD_UNIFIED_ROLES)[number];

const ROLE_DISPLAY_NAMES: Record<StandardUnifiedRole, string> = {
  inbox: 'Inbox',
  sent: 'Sent',
  drafts: 'Drafts',
  spam: 'Spam',
  trash: 'Trash',
  archive: 'Archive',
};

export function getStandardRoleDisplayName(role: StandardUnifiedRole): string {
  return ROLE_DISPLAY_NAMES[role];
}
